import { Filters } from '@/stores/station/station-model';
import { Readable } from 'stream';
import csv from 'csv-parser';
import { testData } from '@/stores/test_data/stations_csv';
import { dateRangesOverlap } from '@/util/datetime';
import { Bundesland, StationMetadataDto, StationMetadataType } from '@/api/out';

interface csvData {
  station: number;
  Synopstationsnummer: number;
  Stationsname: string;
  'Länge [°E]': number;
  'Breite [°N]': number;
  'Höhe [m]': number;
  Startdatum: string;
  Enddatum: string;
  Bundesland: string;
  Sonnenschein: string;
  Globalstrahlung: string;
  Synop: string;
}

// Add dummy combined station to make sure it displays drop-down in table
const mockStation: StationMetadataDto = {
  id: '1',
  name: 'teststation with combined',
  valid_from: '1983-11-11',
  valid_to: '2021-11-11',
  lat: 48,
  lon: 12,
  state: 'Tirol' as Bundesland,
  altitude: 700,
  sub_stations: [],
  has_sunshine: true,
  has_global_radiation: true,
  type: StationMetadataType.Combined,
};
const combinedStation: StationMetadataDto = Object.assign({}, mockStation);
if (combinedStation.sub_stations) {
  combinedStation.sub_stations.push(
    Object.assign(
      {},
      {
        ...mockStation,
        sub_stations: [],
        id: '2',
        valid_to: '1999-01-01',
        type: StationMetadataType.SubStation,
      }
    )
  );
  combinedStation.sub_stations.push(
    Object.assign(
      {},
      {
        ...mockStation,
        sub_stations: [],
        id: '3',
        valid_from: '1999-01-01',
        type: StationMetadataType.SubStation,
      }
    )
  );
}

// define global variable that gets filled with csv data
let stationData: StationMetadataDto[] = [];

export async function getMockDataWithFilter(
  filters: Filters
): Promise<StationMetadataDto[]> {
  let data = await getMockData();
  await delay(500);
  data.push(combinedStation);

  data = data.filter((station) => stationMatches(station, filters));
  const dateRange = [filters.startDate, filters.endDate] as [Date, Date];
  data.forEach((station) => {
    if (station.sub_stations) {
      station.sub_stations.forEach((sub) => {
        if (dateRange === undefined) {
          sub.matches = undefined;
        } else {
          sub.matches = dateRangesOverlap(
            [new Date(sub.valid_from), new Date(sub.valid_to)],
            dateRange
          );
        }
      });
    }
  });
  return [...new Set(data)];
}

function stationMatches(
  station: StationMetadataDto,
  filters: Filters
): boolean {
  const allowed = [];
  if (station.altitude && filters.altitude) {
    allowed.push(
      station.altitude >= filters.altitude[0] &&
        station.altitude <= filters.altitude[1]
    );
  }
  if (filters.name) {
    allowed.push(station.name.includes(filters.name));
  }
  if (filters.states && station.state) {
    allowed.push(filters.states.includes(station.state));
  }
  if (!station.has_global_radiation && filters.onlyWithGlobalRadiation) {
    return false;
  }
  if (!station.has_sunshine && filters.onlyWithSunshine) {
    return false;
  }
  if (filters.startDate && filters.endDate) {
    allowed.push(
      dateRangesOverlap(
        [new Date(station.valid_from), new Date(station.valid_to)],
        [filters.startDate, filters.endDate]
      )
    );
  }
  return !allowed.includes(false);
}

async function getMockData(): Promise<StationMetadataDto[]> {
  // return getMockData(); alternative mock data
  const data = getParseResult();
  if (data.length > 0) {
    return data;
  } else {
    parseCsv();
    while (getParseResult().length === 0) {
      await delay(500);
    }
    return getParseResult();
  }
}

async function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function getParseResult(): StationMetadataDto[] {
  return stationData;
}

export function parseCsv(): void {
  if (stationData.length === 0) {
    const results: csvData[] = [];
    const stationDataTemp: StationMetadataDto[] = [];
    testDataStream()
      .pipe(csv())
      .on('data', (data) => results.push(data))
      .on('end', () => {
        results.forEach((row) => {
          stationDataTemp.push({
            id: row.station.toString(),
            name: row.Stationsname,
            valid_from: row.Startdatum,
            valid_to: row.Enddatum,
            state: row.Bundesland as Bundesland,
            lat: row['Breite [°N]'],
            lon: row['Länge [°E]'],
            altitude: row['Höhe [m]'],
            has_sunshine: row.Sonnenschein === 'ja',
            has_global_radiation: row.Globalstrahlung === 'ja',
            sub_stations: [],
            matches: true,
            type: 'INDIVIDUAL' as StationMetadataType,
          });
        });
        stationData = stationDataTemp;
      });
  }
}

export function testDataStream(): Readable {
  // eslint-disable-next-line
  const Readable = require('stream').Readable;

  const s = new Readable();
  s.push(testData()); // the string you want
  s.push(null); // indicates end-of-file basically - the end of the stream
  return s;
}
